/**
 * Stripe Connect Status Management
 * Centralized functions for checking and managing Connect account status
 */

import SettingsModel from "../firebase/settingsModel";

// TypeScript-style interfaces for documentation
/**
 * @typedef {Object} LandlordConnectStatus
 * @property {boolean} chargesEnabled - Can accept payments
 * @property {boolean} payoutsEnabled - Can receive payouts
 * @property {boolean} detailsSubmitted - Has submitted required info
 * @property {boolean} requirementsCompleted - All requirements met
 * @property {string[]} requirementsCurrentlyDue - What needs to be done now
 * @property {string[]} requirementsEventuallyDue - What needs to be done later
 * @property {string[]} requirementsPastDue - Overdue requirements
 * @property {string[]} requirementsPendingVerification - Awaiting verification
 * @property {boolean} deauthorized - Account disconnected
 * @property {string} lastUpdated - ISO timestamp
 */

/**
 * Check landlord Connect status using backend API
 * @param {string} landlordId - The landlord's user ID
 * @returns {Promise<Object>} Connect status object
 */
export const checkLandlordConnectStatus = async (landlordId) => {
  try {
    const API_BASE_URL =
      process.env.REACT_APP_API_URL || "http://localhost:4000/api";

    // Normalize API_BASE_URL - remove trailing slash
    const baseUrl = API_BASE_URL.replace(/\/$/, "");
    // Build URL - if baseUrl already includes /api, don't add it again
    const url = baseUrl.includes("/api")
      ? `${baseUrl}/connect/landlords/${landlordId}/status`
      : `${baseUrl}/api/connect/landlords/${landlordId}/status`;
    if (process.env.NODE_ENV === "development") {
      console.log("[connectStatus] Calling backend API:", url);
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await getAuthToken()}`,
      },
    });

    if (!response.ok) {
      // No Connect landlord record yet (typical for new/free accounts) - not an error.
      if (response.status === 404) {
        return {
          canAcceptPayments: false,
          canReceivePayouts: false,
          isFullyOnboarded: false,
          pendingRequirements: [],
          futureRequirements: [],
          pastDueRequirements: [],
          pendingVerification: [],
          deauthorized: false,
          lastUpdated: null,
          status: "incomplete",
          rawStatus: {},
        };
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const status = await response.json();

    // Handle nested response structure: {success: true, data: {...}}
    const statusData = status.data || status;

    // Map backend response to frontend format
    return {
      canAcceptPayments: statusData.chargesEnabled || false,
      canReceivePayouts: statusData.payoutsEnabled || false,
      isFullyOnboarded: statusData.requirementsCompleted || false,
      pendingRequirements: statusData.requirementsCurrentlyDue || [],
      futureRequirements: statusData.requirementsEventuallyDue || [],
      pastDueRequirements: statusData.requirementsPastDue || [],
      pendingVerification: statusData.requirementsPendingVerification || [],
      deauthorized: statusData.deauthorized || false,
      lastUpdated: statusData.lastUpdated,
      status: getStatusFromBackend(statusData),
      rawStatus: statusData,
    };
  } catch (error) {
    // Log all errors including expected fallback cases for debugging
    console.error("[connectStatus] Error checking Connect status:", error);
    console.error("[connectStatus] Error details:", {
      message: error.message,
      stack: error.stack,
      landlordId: landlordId,
    });
    return {
      canAcceptPayments: false,
      canReceivePayouts: false,
      isFullyOnboarded: false,
      pendingRequirements: [],
      futureRequirements: [],
      pastDueRequirements: [],
      pendingVerification: [],
      deauthorized: false,
      lastUpdated: null,
      status: "error",
      error: error.message,
    };
  }
};

/**
 * Determine status from backend response
 * @param {Object} status - Backend status response
 * @returns {string} Status string
 */
const getStatusFromBackend = (status) => {
  if (status.deauthorized) return "deauthorized";
  if (status.chargesEnabled && status.payoutsEnabled) return "active";
  if (status.requirementsCurrentlyDue?.length > 0) return "pending";
  if (status.requirementsPastDue?.length > 0) return "past_due";
  if (status.requirementsPendingVerification?.length > 0)
    return "pending_verification";
  return "incomplete";
};

/**
 * Get Connect account ID for a landlord
 * Fetches from Firebase user settings
 * @param {string} landlordId - The landlord's user ID
 * @returns {Promise<string|null>} Connect account ID or null
 */
const getConnectAccountId = async (landlordId) => {
  try {
    const userSettings = await SettingsModel.getByUserId(landlordId);
    return userSettings?.paymentSetup?.stripeAccountId || null;
  } catch (error) {
    console.error("Error getting Connect account ID:", error);
    return null;
  }
};

/**
 * Get UI state based on Connect status
 * @param {Object} status - Connect status object
 * @returns {Object} UI state configuration
 */
export const getConnectStatusUI = (status) => {
  if (!status.canAcceptPayments) {
    return {
      showConnectButton: true,
      showRentPaymentForm: false,
      message:
        "Complete Stripe Connect KYC verification to enable payment acceptance",
      buttonText: "Set Up Payment Processing",
      alertType: "info",
      alertMessage: "KYC verification required",
    };
  } else if (status.pendingRequirements.length > 0) {
    return {
      showConnectButton: true,
      showRentPaymentForm: false,
      message: `Complete setup: ${status.pendingRequirements.join(", ")}`,
      buttonText: "Complete Setup",
      alertType: "warning",
      alertMessage: "Additional verification required",
    };
  } else {
    return {
      showConnectButton: false,
      showRentPaymentForm: true,
      message: "Payment processing is active and ready",
      buttonText: null,
      alertType: "success",
      alertMessage: "Online payments enabled",
    };
  }
};

/**
 * Create new onboarding link for a landlord using backend API
 * @param {string} landlordId - The landlord's user ID
 * @returns {Promise<Object>} Onboarding link result
 */
export const createOnboardingLink = async (landlordId) => {
  try {
    const API_BASE_URL =
      process.env.REACT_APP_API_URL || "http://localhost:4000/api";
    const response = await fetch(
      `${API_BASE_URL}/connect/landlords/${landlordId}/onboarding-link`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();

    if (result.success) {
      return {
        success: true,
        onboardingUrl: result.data.onboardingUrl,
        expiresAt: result.data.expiresAt,
      };
    } else {
      throw new Error(result.error || "Failed to create onboarding link");
    }
  } catch (error) {
    console.error("Error creating onboarding link:", error);
    return {
      success: false,
      error: handleConnectError(error),
    };
  }
};

/**
 * Handle Connect errors gracefully with improved error messages
 * @param {Error} error - The error object
 * @returns {string} User-friendly error message
 */
export const handleConnectError = (error) => {
  const errorMessages = {
    "Connect not enabled":
      "Payment processing is temporarily unavailable. Please try again later.",
    "Landlord not found": "Account not found. Please contact support.",
    "Invalid request data": "Please check your information and try again.",
    "Failed to create Connect account":
      "Unable to connect payment account. Please try again.",
    "Failed to fetch":
      "Unable to connect to the payment service. Please check your internet connection and try again.",
    "Not a valid URL": "Invalid API configuration. Please contact support.",
    "Invalid token": "Authentication failed. Please log in again.",
    "HTTP 401": "Authentication failed. Please log in again.",
    "HTTP 403": "Access denied. Please contact support.",
    "HTTP 404": "Service not found. Please contact support.",
    "HTTP 500": "Server error. Please try again later.",
  };

  // Check for specific error patterns
  for (const [pattern, message] of Object.entries(errorMessages)) {
    if (error.message.includes(pattern)) {
      return message;
    }
  }

  // Default fallback
  return "Unable to connect payment account. Please try again.";
};

/**
 * Initialize Connect account for a landlord using backend API
 * @param {string} landlordId - The landlord's user ID
 * @returns {Promise<Object>} Initialization result
 */
export const initializeConnectAccount = async (landlordId) => {
  try {
    const API_BASE_URL =
      process.env.REACT_APP_API_URL || "http://localhost:4000/api";
    const response = await fetch(
      `${API_BASE_URL}/connect/landlords/${landlordId}/init`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await getAuthToken()}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();

    if (result.success) {
      return {
        success: true,
        onboardingUrl: result.data.onboardingUrl,
        accountId: result.data.accountId,
      };
    } else {
      throw new Error(result.error || "Failed to initialize Connect account");
    }
  } catch (error) {
    console.error("Error initializing Connect:", error);
    return {
      success: false,
      error: handleConnectError(error),
    };
  }
};

/**
 * Update Firebase settings with Connect account information
 * @param {string} landlordId - The landlord's user ID
 * @param {string} accountId - The Stripe Connect account ID
 * @param {Object} accountStatus - The account status from Stripe
 * @returns {Promise<Object>} Update result
 */
export const updateConnectAccountInSettings = async (
  landlordId,
  accountId,
  accountStatus
) => {
  try {
    // Update userSettings for frontend compatibility
    const settingsUpdates = {
      "paymentSetup.stripeAccountId": accountId,
      "paymentSetup.onboardingComplete":
        accountStatus.details_submitted || false,
      "paymentSetup.bankAccountConnected":
        accountStatus.payouts_enabled || false,
      "paymentSetup.isComplete":
        accountStatus.charges_enabled && accountStatus.payouts_enabled,
      "paymentSetup.lastUpdated": new Date(),
    };

    await SettingsModel.updateSettings(landlordId, settingsUpdates);

    // Also update the main user document for webhook compatibility
    const { doc, updateDoc } = await import("firebase/firestore");
    const { db } = await import("../../helpers/firebase");

    const userRef = doc(db, "users", landlordId);
    const connectStatusUpdates = {
      stripeConnectId: accountId,
      "connectStatus.charges_enabled": accountStatus.charges_enabled || false,
      "connectStatus.payouts_enabled": accountStatus.payouts_enabled || false,
      "connectStatus.details_submitted":
        accountStatus.details_submitted || false,
      "connectStatus.updatedAt": new Date().toISOString(),
    };

    await updateDoc(userRef, connectStatusUpdates);

    return {
      success: true,
      message: "Payment settings updated successfully",
    };
  } catch (error) {
    console.error("Error updating Connect account in settings:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Handle onboarding completion
 * @param {string} landlordId - The landlord's user ID
 * @returns {Promise<Object>} Completion result
 */
export const handleOnboardingReturn = async (landlordId) => {
  try {
    // Check updated status
    const status = await checkLandlordConnectStatus(landlordId);

    if (status.canAcceptPayments) {
      return {
        success: true,
        message: "Payment account connected successfully!",
        status: "complete",
        connectStatus: status,
      };
    } else if (status.pendingRequirements.length > 0) {
      return {
        success: false,
        message: `Please complete: ${status.pendingRequirements.join(", ")}`,
        status: "incomplete",
        connectStatus: status,
      };
    } else {
      return {
        success: false,
        message: "Please complete your payment account setup",
        status: "incomplete",
        connectStatus: status,
      };
    }
  } catch (error) {
    console.error("Error handling onboarding return:", error);
    return {
      success: false,
      message: "Failed to verify payment account setup",
      status: "error",
      error: error.message,
    };
  }
};

// Helper function to get auth token from Firebase
const getAuthToken = async () => {
  try {
    const { getAuth } = await import("firebase/auth");
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const token = await user.getIdToken();
    return token;
  } catch (error) {
    console.error("Error getting auth token:", error);
    throw new Error("Failed to get authentication token");
  }
};
